import { useEffect, useState } from "react";
import { useAuth } from "../auth/AuthContext.js";
import { api, ApiError } from "../api/client.js";
import type { ImagePersonaOption } from "../api/types.js";
import { resolveImageModeStep } from "../pages/generate/image/image-ui.js";

/**
 * Image Mode's account/persona setup gate. It explains the three real
 * preconditions (verified email, a workspace, a Generate-ready persona) and
 * otherwise renders its children. It deliberately has no license/plan step
 * and no Image-specific switch — see `resolveImageModeStep`.
 */
export function ImageModeGate({ tenantId, children }: { readonly tenantId: string | null; readonly children: React.ReactNode }) {
  const { user } = useAuth();
  const [personas, setPersonas] = useState<ImagePersonaOption[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (tenantId === null || user === null || (!user.emailVerified && !user.isPlatformOwner)) {
      return;
    }
    let cancelled = false;
    setPersonas(null);
    setLoadError(null);
    api
      .get<{ personas: ImagePersonaOption[] }>(`/images/${encodeURIComponent(tenantId)}/personas`)
      .then((result) => {
        if (!cancelled) setPersonas(result.personas);
      })
      .catch((error) => {
        if (!cancelled) setLoadError(error instanceof ApiError ? error.message : "Could not load your workspace's personas.");
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, user?.emailVerified, user?.isPlatformOwner]);

  if (user === null) {
    return <>{children}</>;
  }
  const step = resolveImageModeStep({ isPlatformOwner: user.isPlatformOwner, emailVerified: user.emailVerified, tenantId, personas });
  if (step === "ready") {
    return <>{children}</>;
  }
  if (step === "loading") {
    return loadError !== null ? <div className="error-banner">{loadError}</div> : <div className="empty-state">Checking your workspace's readiness…</div>;
  }
  return (
    <div className="card" data-testid="image-mode-gate">
      <h2>Get set up for Image mode</h2>
      {step === "verify_email" && (
        <p>
          Your account's email address hasn't been verified yet. Visit <a href="/resend-verification">this page</a> for details, or contact an operator to verify your account.
        </p>
      )}
      {step === "workspace" && (
        <p>
          You don't belong to a workspace yet. <a href="/workspaces/new">Create one</a> or ask a workspace ADMIN to invite you.
        </p>
      )}
      {step === "persona" && (
        <p>
          You don't have a ready Persona yet — a Persona needs a completed, trained Phota Profile before images can be created. Go to <a href="/generate/personas">GENERATE → PERSONAS</a> to create one and upload its reference photos.
        </p>
      )}
    </div>
  );
}
